/*
 * Copyright (c) 2025. Bolmate B.V.
 */

import useNotifications from "../useNotifications";
import {NotificationInterval, NotificationType, SubscriptionModuleName} from "../../../util/enums";
import React, {MouseEventHandler, useContext, useEffect, useMemo, useRef} from "react";
import {AppContext} from "../../misc/appContext";
import useRight from "../../misc/useRight";
import {AccessKey} from "../../../util/interfaces";
import Notification, {NotificationFields} from "../../../models/notificationModel";
import {ReviewNotificationSettings} from "../../../util/reviewAnalyticsUtil";
import {createErrorMessage} from "../../../util/utils";
import {ResponseData, showDialog, validateEmail} from "@dvrd/dvr-controls";
import {Rating} from "../../../util/types";
import ReviewNotifications from "./reviewNotifications";
import PageChangePermission from "../../misc/pageChangePermission";

interface Props {
    onSendTestNotification: (notification: Notification) => MouseEventHandler;
    onPulseModule: VoidFunction;
}

const DEBOUNCE = 2000;

export default function ReviewNotificationsController(props: Props) {
    const context = useContext(AppContext);
    const {account} = context.accountContext;
    const {user} = context.userContext;
    const {onSendTestNotification, onPulseModule} = props;
    const [canEdit] = useRight(AccessKey.RATING_SETTINGS);
    const [notifications, setNotifications, loading, setLoading, _] = useNotifications(NotificationType.REVIEWS);
    const debouncers = useRef<Record<string, number | undefined>>({});
    const moduleActive = useMemo(() => {
        return !!user?.subscription?.hasActiveModule(SubscriptionModuleName.NOTIFICATIONS);
    }, [user]);

    function onAddNotification() {
        if (!account || !canEdit) return;
        if (!moduleActive) return onPulseModule();
        setLoading(true);
        const notification = new Notification({
            notification_type: NotificationType.REVIEWS,
            interval: NotificationInterval.DAY,
            email_addresses: [account.email],
            flex: new ReviewNotificationSettings({ratings: [1, 2, 3, 4, 5], eans: ['all']}).dump(),
            account_id: account.id,
        });
        notification.create((notification: Notification, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) setNotifications(notifications.concat(notification));
            else
                showDialog(createErrorMessage(data.message ?? 'Het toevoegen van de notificatie is niet gelukt.'),
                    'Toevoegen mislukt');
        })
    }

    function onChangeInterval(notificationID: string) {
        return function(value: NotificationInterval) {
            onChangeNotification(notificationID, 'interval', value);
        }
    }

    function onChangeEmailAddresses(notificationID: string) {
        return function(value: string) {
            const emails = value.split(',');
            onChangeNotification(notificationID, 'email_addresses', emails);
        }
    }

    function onSelectRating(notificationID: string, rating: Rating) {
        return function() {
            for (const notification of notifications) {
                if (notification.id !== notificationID) continue;

                const reviewSettings = new ReviewNotificationSettings(notification.flex);
                if (reviewSettings.ratings.has(rating)) reviewSettings.ratings.delete(rating);
                else reviewSettings.ratings.add(rating);
                onChangeNotification(notificationID, 'flex', reviewSettings.dump());
                break;
            }
        }
    }

    function onChangeProducts(notificationID: string, selectedProducts: Array<string>) {
        if (selectedProducts.includes('all') && selectedProducts.length === 1) selectedProducts = [];
        for (const notification of notifications) {
            if (notification.id !== notificationID) continue;
            const flex = new ReviewNotificationSettings({...notification.flex, eans: selectedProducts}).dump();
            onChangeNotification(notificationID, 'flex', flex);
            break;
        }
    }

    function onChangeNotification(notificationID: string, key: keyof NotificationFields, value: any) {
        clearTimeout(notificationID);
        for (const notification of notifications) {
            if (notification.id !== notificationID) continue;
            notification.setField(key, value);
            debouncers.current[notificationID] = window.setTimeout(notification.update, DEBOUNCE);
            break
        }
        setNotifications(notifications.slice());
    }

    function onDeleteNotification(notificationID: string) {
        return function() {
            if (!canEdit) return;
            showDialog('Weet je zeker dat je deze notificatie wilt verwijderen?', 'Notificatie verwijderen',
                ['Nee', {label: 'Ja', onClick: onConfirmDelete(notificationID), primary: false}]);
        }
    }

    function onConfirmDelete(notificationID: string) {
        return function() {
            clearTimeout(notificationID);
            if (!canEdit) return;
            for (let ix = 0; ix < notifications.length; ix++) {
                if (notifications[ix].id !== notificationID) continue;
                setLoading(true);
                notifications[ix].delete((_: Notification, success: boolean, data: ResponseData) => {
                    setLoading(false);
                    if (success)
                        setNotifications(notifications.toSpliced(ix, 1));
                    else
                        showDialog(
                            createErrorMessage(data.message ?? 'Het verwijderen van de notificatie is niet gelukt.'),
                            'Verwijderen mislukt');
                })
                break;
            }
        }
    }

    function hasErrors() {
        return notifications.some((notification: Notification) => {
            return notification.emailAddresses.some((email: string) => !validateEmail(email));
        });
    }

    function clearTimeout(notificationID: string) {
        window.clearTimeout(debouncers.current[notificationID]);
    }

    useEffect(() => {
        return function() {
            for (const value of Object.values(debouncers.current))
                window.clearTimeout(value);
        }
    }, []);

    return (
        <>
            <ReviewNotifications onAddNotification={onAddNotification} onChangeInterval={onChangeInterval}
                                 onChangeEmailAddresses={onChangeEmailAddresses} onChangeProducts={onChangeProducts}
                                 onSelectRating={onSelectRating} onDeleteNotification={onDeleteNotification}
                                 notifications={notifications} loading={loading}
                                 moduleActive={moduleActive} onSendTestNotification={onSendTestNotification}/>
            <PageChangePermission changed={hasErrors()} denyText='Nee, op pagina blijven'
                                  allowText='Ja, pagina verlaten' allowSecondary={true}>
                Je hebt bij één of meer notificaties geen geldig e-mailadres ingevuld. De notificatie kan hierdoor niet
                naar je verzonden worden. Weet je zeker dat je de pagina wilt verlaten?
            </PageChangePermission>
        </>
    );
}